import type { ApiService, Logger } from "@packages/common";
import type { User } from "../user";
import type {
  AddFavoriteApiResultDTO,
  AddFavoriteDTO,
  FavoriteApiResponseDTO,
  FavoriteApiResultDTO,
  FavoriteItemDTO,
  FavoritesDTO,
  FavoritesPageDTO,
  RemoveFavoriteDTO,
} from "./dtos";

import { API, LOGGER } from "@packages/common";
import { Inject, Injectable } from "@webmotors/ioc";

import { FAVORITES_SERVICE } from "./favorites.types";
import { FavoriteVehicleTypeDTO } from "./dtos";

@Injectable(FAVORITES_SERVICE)
export class FavoritesService {
  /** Page size requested from the API. */
  private static readonly DISPLAY_PER_PAGE = 25;

  private readonly logger: Logger;

  constructor(
    @Inject(LOGGER) loggerFactory: Logger,
    @Inject(API) private readonly apiService: ApiService,
  ) {
    this.logger = loggerFactory.create(FAVORITES_SERVICE);
  }

  private getHeaders(user: User | null): Record<string, string> {
    return {
      authorization: user?.authenticationToken ?? "",
    };
  }

  private fetchFavoritesPage(
    user: User,
    vehicleType: FavoriteVehicleTypeDTO,
    page: number,
  ): Promise<FavoriteApiResponseDTO> {
    return this.apiService.get<unknown, FavoriteApiResponseDTO>(
      "/extras/showroom",
      {
        vehicle_type: vehicleType,
        actual_page: page,
        display_per_page: FavoritesService.DISPLAY_PER_PAGE,
        sort: 1,
        site: true,
      },
      {
        headers: {
          ...this.getHeaders(user),
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      },
    );
  }

  private mapResult(
    item: FavoriteApiResultDTO,
    vehicleType: FavoriteVehicleTypeDTO,
  ): FavoriteItemDTO {
    return {
      advertiseId: item.advertise_id,
      guid: item.guid,
      site: true,
      vehicleType,
      brand: item.brand,
      brandId: item.brandId,
      model: item.model,
      modelId: item.modelId,
      version: item.version,
      versionId: item.versionId,
      price: item.price,
      odometer: item.odometer,
      yearFabrication: item.year_fabrication,
      yearModel: item.year_model,
      primaryColor: item.primary_color,
      city: item.city,
      state: item.state,
      sellerType: item.seller_type,
      advertiser: item.advertiser,
      advertiserId: item.advertiserId,
      active: item.active,
      createDate: item.create_date,
      numberPorts: item.number_ports,
      listingType: item.listing_type,
      photos: item.photos?.length
        ? item.photos
        : item.image_path
          ? [item.image_path]
          : [],
      cubicCentimeter: item.cubic_centimeter,
    };
  }

  private async fetchFavorites(
    user: User,
    vehicleType: FavoriteVehicleTypeDTO,
  ): Promise<FavoritesDTO> {
    try {
      const response = await this.fetchFavoritesPage(user, vehicleType, 1);
      const favorites = (response.results ?? []).map((item) =>
        this.mapResult(item, vehicleType),
      );

      return { favorites, count: response.count };
    } catch (error) {
      this.handleError(
        `[FetchFavorites] Error for type ${vehicleType}:`,
        error,
      );
      return { favorites: [], count: 0 };
    }
  }

  /**
   * Fetch a single page of favorites for one vehicle type, including the
   * pagination metadata used to drive infinite scroll on the favorites screen.
   *
   * The favorites API is paginated and caps how many items it returns per
   * request, so the screen loads one page at a time as the user scrolls
   * instead of fetching everything up front (ENO-10086).
   *
   * @param user - The user's session
   * @param vehicleType - Car or bike
   * @param page - Page to fetch (1-based)
   * @returns A promise that resolves to a FavoritesPageDTO object
   */
  async getByType(
    user: User | null,
    vehicleType: FavoriteVehicleTypeDTO,
    page = 1,
  ): Promise<FavoritesPageDTO> {
    if (!user) {
      return { favorites: [], count: 0, page, totalPages: 0 };
    }

    try {
      const response = await this.fetchFavoritesPage(user, vehicleType, page);

      const count = response.count ?? 0;
      const favorites = (response.results ?? []).map((item) =>
        this.mapResult(item, vehicleType),
      );

      // Prefer the page count reported by the API; otherwise derive it from
      // the total count and the configured page size. Note: pagination stops
      // based on loaded-vs-count in the query layer, so this value is only
      // informational and not the authority for fetching subsequent pages.
      const totalPages =
        response.pages ??
        (count > 0
          ? Math.ceil(count / FavoritesService.DISPLAY_PER_PAGE)
          : 0);

      return {
        favorites,
        count,
        page: response.actual_page ?? page,
        totalPages,
      };
    } catch (error) {
      this.handleError(
        `[GetByType] Error for type ${vehicleType} page ${page}:`,
        error,
      );
      return { favorites: [], count: 0, page, totalPages: 0 };
    }
  }

  private handleError(message: string, error: unknown) {
    const errorMsg = (error as Error).message;
    this.logger.error(message, errorMsg);
  }

  /**
   * Fetch all favorites for both cars and bikes for a given user.
   *
   * @param user - The user's session
   * @returns A promise that resolves to a FavoritesDTO object
   */
  async get(user: User | null): Promise<FavoritesDTO> {
    if (!user) {
      this.logger.info("[Get] No JWT provided, returning empty favorites.");
      return { favorites: [], count: 0 };
    }

    try {
      const [carFavorites, bikeFavorites] = await Promise.all([
        this.fetchFavorites(user, FavoriteVehicleTypeDTO.CAR),
        this.fetchFavorites(user, FavoriteVehicleTypeDTO.BIKE),
      ]);

      return {
        favorites: [...carFavorites.favorites, ...bikeFavorites.favorites],
        count: carFavorites.count + bikeFavorites.count,
      };
    } catch (error) {
      this.handleError("[Get] Error fetching user favorites:", error);
      return { favorites: [], count: 0 };
    }
  }

  /**
   * Add a favorite for a user.
   *
   * @param params - AddFavoriteDTO object containing user, id, and vehicleType
   * @returns A promise that resolves to a FavoriteItemDTO
   */
  async add({
    user,
    id,
    vehicleType,
  }: AddFavoriteDTO): Promise<FavoriteItemDTO | null> {
    if (!user) return null;

    try {
      const type = vehicleType.includes("motos")
        ? FavoriteVehicleTypeDTO.BIKE
        : FavoriteVehicleTypeDTO.CAR;

      const data = {
        advertise_id: Number(id),
        vehicle_type: type,
        site: true,
      };

      const response = await this.apiService.post<
        unknown,
        AddFavoriteApiResultDTO
      >("/extras/showroom", data, { headers: this.getHeaders(user) });

      return {
        advertiseId: response.id,
        guid: response.excludeFavoriteKey,
        site: true,
        vehicleType: type,
      };
    } catch (error) {
      this.handleError("[Add] Error adding favorite:", error);
      return null;
    }
  }

  /**
   * Remove a favorite for a user.
   *
   * @param params - RemoveFavoriteDTO object containing guid and user
   * @returns A promise that resolves to the removed favorite's guid
   */
  async remove({ guid, user }: RemoveFavoriteDTO): Promise<string | null> {
    if (!user) return null;

    try {
      const data = { guid };

      const { guid: removedGuid } = await this.apiService.delete<
        unknown,
        { guid: string }
      >("/extras/showroom", data, { headers: this.getHeaders(user) });

      return removedGuid;
    } catch (error) {
      this.handleError("[remove] Error removing favorite:", error);
      return null;
    }
  }
}
